import { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useNavigate } from 'react-router-dom';
import 'ag-grid-enterprise';
import apiServices from '../../services/service';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveOwners, getGridDataByModuleKey, getPriceList } from '../../redux/selector';
import usePriceColumnDefs from './usePriceColumnDefs';
import CustomStatusBar from '../sharedChild/grid/customStatusBar';
import CustomSelectedRowCount from '../sharedChild/grid/customSelectedRowCount';
import gridOptionsConfig from '../sharedChild/grid/gridOptionsConfig';
import useFetchIfNeeded from '../../redux/useFetchIfNeeded';
import { priceInputData } from '../../shared/constants';
import Loader from '../../shared/component/Loader';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import FilterHandler from '../sharedChild/grid/filterHandler';

const PriceListing = () => {
  const stateData = useSelector((state) => state);
  let storeLoading = isLoadingByKey(stateData, 'priceSheetList');
  const navigate = useNavigate();
  const gridRef = useRef();
  const [gridParams, setGridParams] = useState(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(storeLoading);
  const [selectedTheme, setSelectedTheme] = useState('balham');
  const gridContainerRef = useRef();
  const rowData = useSelector(getPriceList);
  const gridOptions = gridOptionsConfig('priceSheetList', navigate);
  const gridData = useSelector(getGridDataByModuleKey('priceSheetList'));
  const owner = useSelector(getActiveOwners);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useFetchIfNeeded(getPriceList, fetchDataAndUpdateStore, {
    key: 'priceSheetList',
    inputData: priceInputData,
  });

  useEffect(() => {
    if (gridContainerRef.current) {
      gridContainerRef.current.style.setProperty(
        'height',
        'calc(100vh - (0px + 138px))',
        'important'
      );
    }
  }, []);

  // const [columnDefs] = useState([
  //   {
  //     headerName: "Actions",
  //     cellRenderer: (params) => {
  //       const paramsValue = { ...params.data };
  //       return (
  //         <OptionButton
  //           rowData={paramsValue}
  //           handleClickOpenEditForm={() => handleClickOpenEditForm(paramsValue)}
  //           handleClickDelete={() => handleClickDelete(paramsValue)}
  //         />
  //       );
  //     },
  //     field: "actions",
  //     width: 100,
  //     pinned: "left",
  //     resizable: false,
  //   },
  //   { headerName: "Price Book Name", field: "name" },
  //   { headerName: "Price Book Owner", field: "priceBookOwner" },
  //   { headerName: "Pricing Model", field: "pricingModel" },
  //   { headerName: "From Range", field: "fromRange" },
  //   { headerName: "To Range", field: "toRange" },
  //   { headerName: "Discount", field: "discount" },
  //   { headerName: "Status", field: "status" },
  //   {
  //     headerName: "Active",
  //     field: "productActive",
  //     flex: 1,
  //     cellRenderer: (p) => (p.value ? "Yes" : "No"),
  //   },
  // ]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      enableRowGroup: true,
    }),
    []
  );

  const handleClickOpenEditForm = (paramsValue) => {
    navigate('/products/priceSheet/editPriceSheet', {
      state: paramsValue,
    });
  };

  const handleOpenViewForm = (paramsValue) => {
    navigate('/products/priceSheet/viewPriceSheet', {
      state: paramsValue,
    });
  };

  const handleClickDelete = (paramsValue) => {
    setLoading(true);
    let data;
    data = {
      id: [paramsValue.id],
    };
    apiServices
      .deletePrice(data)
      .then(async (response) => {
        if (response.status === 200) {
          toast.success('Price Deleted Successfully!', {
            position: 'top-right',
          });
          if (paramsValue.id === undefined) {
            document.getElementById('massDeleteModal').click();
          }
          const columnName = 'prodcutName';
          const orderType = 'desc';
          const data = {
            pageSize: parseInt(process.env.REACT_APP_DEFAULT_PAGESIZE),
            pageNumber: 1,
            columnName,
            orderType,
            filterJson: null,
          };

          try {
            // ✅ Await the dispatch before navigation
            await dispatch(fetchDataAndUpdateStore({ key: 'priceSheetList', inputData: data }));
          } catch (error) {
            toast.error('Something went wrong while updating the list.', {
              position: 'top-right',
            });
          }
          setLoading(false);
        } else {
          setLoading(false);
          toast.error('Error deleting record', {
            position: 'top-right',
          });
        }
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
        setLoading(false);
      });
  };

  const columnDefs = usePriceColumnDefs({
    handleClickDelete,
    handleClickOpenEditForm,
    handleOpenViewForm,
    owner
  });

  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
    setGridParams(params);
  }, []);

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressPivotMode: true, // 👈 Hide Pivot Mode toggle
            suppressRowGroups: false, // ✅ Allow row grouping
            suppressValues: false, // ✅ Allow aggregation if needed
            suppressPivots: true, // 👈 Hide the pivot section for columns
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
      position: 'left',
      defaultToolPanel: 'none', //show off filter tab bydefault.
    };
  }, []);

  const handleGridReady = (params) => {
    gridRef.current = params;
    setGridParams(params);
    const columnOrderFromStore = gridData?.columnState;

    if (columnOrderFromStore) {
      params.columnApi.applyColumnState({
        state: columnOrderFromStore,
        applyOrder: true,
      });
    } else {
      // apply default column visibility if no saved state
      const fieldsToHide = [];

      const defaultState = params.columnApi.getColumnState().map((col) => ({
        ...col,
        hide: fieldsToHide.includes(col.colId),
      }));

      params.columnApi.applyColumnState({
        state: defaultState,
        applyOrder: true,
      });
    }
  };

  useEffect(() => {
    if (
      gridRef.current?.api &&
      gridData?.priceSheetList?.length &&
      gridData?.inputPayload?.filterJson
    ) {
      try {
        // Parse filterJson
        const filterJson = JSON.parse(gridData.inputPayload.filterJson);

        // Extract campaign IDs
        const campaignIds = filterJson?.campaignId?.conditions
          ? filterJson.campaignId.conditions.map((condition) => condition.filter)
          : filterJson?.campaignId?.filter
            ? [filterJson.campaignId.filter]
            : [];

        // Extract tag IDs
        const tagIds = filterJson?.tagIds?.conditions
          ? filterJson.tagIds.conditions.map((condition) => condition.filter)
          : filterJson?.tagIds?.filter
            ? [filterJson.tagIds.filter]
            : [];

        // Create a mapping of campaignId -> campaignName
        const campaignMapping = rowData.reduce((acc, campaign) => {
          acc[campaign.id] = campaign.campaignName;
          return acc;
        }, {});

        // Get campaign names from matched IDs
        const matchedCampaignNames = campaignIds.map(
          (id) => campaignMapping[id] || 'Unknown Campaign'
        );

        // Update contacts with campaignName and tags
        const updatedContacts = gridData.account.map((account) => ({
          ...account,
          campaignName: matchedCampaignNames, // Correct format
          // tags: matchedTagNames, // Flattened array
        }));

        // Update grid data
        const updatedGridData = {
          ...gridData,
          account: updatedContacts,
        };

        // Update the grid with new data
        gridRef.current.api.setGridOption('rowData', updatedGridData.account);
        gridRef.current.api.refreshCells();
      } catch (error) {
        console.error('Error parsing filterJson:', error);
      }
    }
  }, [gridData, rowData]);

  const filterDefaultSetRef = useRef(null);

  const handleFilterChanged = useCallback(
    (event) => {
      if (filterDefaultSetRef.current) {
        filterDefaultSetRef.current = false;
      } else {
        let filterModel = event.api.getFilterModel();

        // Create mappings for campaign names and tag names to their IDs
        const productMapping = {};
        if (rowData?.length) {
          rowData.forEach((item) => {
            productMapping[item.accountName] = item.id;
          });
        }

        const updatedFilterModel = Object.keys(filterModel).reduce((acc, dynamicFilterKey) => {
          let filterData = filterModel[dynamicFilterKey];

          if (dynamicFilterKey === 'campaignId') {
            // Convert campaign names to campaign IDs
            const idValues = filterData.values
              .map((name) => productMapping[name])
              .filter((id) => id !== undefined);

            if (idValues.length > 0) {
              acc[dynamicFilterKey] = {
                filterType: 'text',
                operator: 'OR',
                conditions: idValues.map((id) => ({
                  filterType: 'text',
                  type: 'contains',
                  filter: id, // Ensure we send ID, not name
                })),
              };
            }
          }

          // Keep Other Filters Unchanged
          else {
            acc[dynamicFilterKey] = filterData;
          }

          return acc;
        }, {});

        const payload = {
          ...gridData?.inputPayload,
          filterJson: JSON.stringify(updatedFilterModel),
          pageNumber: 1, // Reset to first page on filter change
        };

        dispatch(
          fetchDataAndUpdateStore({
            key: 'priceSheetList',
            inputData: payload,
          })
        );
      }
    },
    [dispatch, gridData?.inputPayload, rowData]
  );

  return (
    <div className="cutome_ag_theme">
      {loading && <Loader />}
      <div
        className={`ag-theme-${selectedTheme} tbl_height`}
        style={{ width: '100%', height: `100%` }}
      >
        <div ref={gridContainerRef} className="ag-grid-container">
          <AgGridReact
            ref={gridRef}
            domLayout="fixedHeight"
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection="single"
            enableRangeSelection={true}
            suppressExcelExport={true}
            suppressRowClickSelection={true}
            groupSelectsChildren={true}
            enableRowGroup={true}
            pivotPanelShow="always"
            popupParent={document.body}
            sortingOrder={['asc', 'desc', null]}
            rowDragManaged={true}
            suppressMoveWhenRowDragging={true}
            rowGroupPanelShow={"always"}
            sideBar={sideBar}
            gridOptions={gridOptions}
            onGridReady={handleGridReady}
            onFilterChanged={handleFilterChanged}
          />
          {gridParams && (
            <FilterHandler
              params={gridParams}
              gridData={gridData}
              columnDefs={columnDefs}
              filterDefaultSetRef={filterDefaultSetRef}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceListing;
